import { imprimirUmaVez, type DocumentoImpressao, type OpcoesImpressaoTotem } from './impressao-totem'

export type TipoImpressaoPedidoTotem = 'NENHUMA' | 'RESUMO' | 'NFCE'
export type DocumentoPedidoTotem = 'SENHA' | 'RESUMO' | 'NFCE'

export interface RespostaDocumentoPedido {
  documentos: DocumentoImpressao[]
  situacaoNota?: string | null
}

export interface DependenciasAcompanhamento {
  buscar: (idPedido: string, documento: DocumentoPedidoTotem) => Promise<RespostaDocumentoPedido>
  servidor: string | null
  esperar?: (ms: number) => Promise<void>
  agora?: () => number
}

export const PRAZO_POR_DOCUMENTO_MS: Record<DocumentoPedidoTotem, number> = {
  SENHA: 2 * 60_000,
  RESUMO: 2 * 60_000,
  NFCE: 10 * 60_000,
}

const INTERVALO_MS = 3000
const FALHAS_DE_IMPRESSORA_TOLERADAS = 3

const emAcompanhamento = new Map<string, Promise<void>>()
const pedidosComFalha = new Set<string>()
const ouvintes = new Set<() => void>()

function marcarFalha(idPedido: string): void {
  if (pedidosComFalha.has(idPedido)) return

  pedidosComFalha.add(idPedido)
  for (const ouvinte of ouvintes) ouvinte()
}

export function falhouImpressaoDoPedido(idPedido: string): boolean {
  return pedidosComFalha.has(idPedido)
}

export function aoMudarFalhaDeImpressao(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte)

  return () => {
    ouvintes.delete(ouvinte)
  }
}

export function haImpressaoEmAndamento(): boolean {
  return emAcompanhamento.size > 0
}

/*
 * As tres opcoes sao independentes: nao da para derivar do enum legado, que colapsa
 * "so senha" em NENHUMA e perde o resumo quando ha nota. PERGUNTAR fica de fora — quem
 * decide o papel da nota e o cliente, na tela de confirmacao.
 */
export function documentosDoPedido(opcoes: OpcoesImpressaoTotem): DocumentoPedidoTotem[] {
  const documentos: DocumentoPedidoTotem[] = []

  if (opcoes.imprimirSenha) documentos.push('SENHA')
  if (opcoes.imprimirResumo) documentos.push('RESUMO')
  if (opcoes.nota === 'SEMPRE') documentos.push('NFCE')

  return documentos
}

// Roda fora do ciclo da tela: a NFC-e pode autorizar quando o totem já voltou ao catálogo
export function acompanharImpressaoDoPedido(
  idPedido: string,
  opcoes: OpcoesImpressaoTotem,
  deps: DependenciasAcompanhamento,
): Promise<void> {
  const emCurso = emAcompanhamento.get(idPedido)
  if (emCurso) return emCurso

  const esperar = deps.esperar ?? ((ms: number) => new Promise<void>((resolver) => setTimeout(resolver, ms)))
  const agora = deps.agora ?? Date.now

  const execucao = (async (): Promise<void> => {
    for (const documento of documentosDoPedido(opcoes)) {
      const limite = agora() + PRAZO_POR_DOCUMENTO_MS[documento]
      const ultimaResposta: { situacaoNota?: string | null } = {}
      let falhasDeImpressora = 0

      for (;;) {
        const desfecho = await imprimirUmaVez(
          `${idPedido}:${documento}`,
          async () => {
            try {
              const resposta = await deps.buscar(idPedido, documento)
              ultimaResposta.situacaoNota = resposta.situacaoNota ?? null

              return resposta.documentos
            } catch {
              // Erro passageiro ao buscar vale como "ainda não disponível": tenta de novo até o prazo
              ultimaResposta.situacaoNota = null
              return []
            }
          },
          deps.servidor,
        )

        if (desfecho === 'IMPRESSO' || desfecho === 'JA_IMPRESSO') break

        // Nota recusada: não há o que imprimir, e a tela já mostra o motivo
        if (desfecho === 'SEM_DOCUMENTO' && ultimaResposta.situacaoNota === 'ERRO') break

        // A impressora pode ter recebido parte do cupom: repetir sem limite sairia em pedaços
        falhasDeImpressora = desfecho === 'FALHOU' ? falhasDeImpressora + 1 : 0

        if (falhasDeImpressora >= FALHAS_DE_IMPRESSORA_TOLERADAS || agora() >= limite) {
          marcarFalha(idPedido)
          break
        }

        await esperar(INTERVALO_MS)
      }
    }
  })().finally(() => {
    emAcompanhamento.delete(idPedido)
  })

  emAcompanhamento.set(idPedido, execucao)

  return execucao
}
